import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import { getCookie } from '../lib/cookies';
import {
  type GravityTokenClaims,
  getGravityUserIdFromCookie,
  getGravityUserOrgIdFromCookie,
  getGravityUserPlanFromCookie,
  getGravityUserRegionFromCookie,
  getGravityUserStatusFromCookie,
} from '../domains/auth/utils/gravityTokenClaims';
import { clearOrgPlanningCommandSequencers } from '../domains/orgchart/hooks/orgPlanningCommandSequencer';
import { invalidateAllProtectedPlanningGenerations } from '../domains/orgchart/hooks/protectedPlanningGeneration';
import { useOrgPlanningScenarioStore } from '../domains/orgchart/stores/orgPlanningScenarioStore';
import { useSuccessionOptimisticSlateStore } from '../domains/orgchart/stores/successionOptimisticSlateStore';
import { observeAtlasInteractionRequest } from '../infrastructure/observability/atlasInteractionNetworkTiming';
// Auth types (kept for compatibility)

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  createdAt: Date;
}

export interface AuthResponse {
  user: User;
  token: string;
  expiresAt: Date;
}

export type JWTPayload = Required<GravityTokenClaims>;

export function useLogin() {
  return useMutation<AuthResponse, Error, any>({
    mutationFn: async () => {
      throw new Error('Legacy login removed - use iframe auth instead');
    },
  });
}

export function useLogout() {
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      // Just clear local state, no API call needed
    },
    onSuccess: () => {
      // Protected planning values are memory-only. Abort queued writes and
      // purge every local representation before clearing auth/query state so
      // a late response cannot expose the previous principal's draft.
      invalidateAllProtectedPlanningGenerations();
      clearOrgPlanningCommandSequencers();
      useSuccessionOptimisticSlateStore.getState().clearAll();
      useOrgPlanningScenarioStore.getState().clearAll();
      clearAuth();
      queryClient.clear();
    },
  });
}

export function useAuthenticatedFetch() {
  return useCallback(async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    // Get token from cookie and add to Authorization header
    const token = getCookie('gravity_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    return observeAtlasInteractionRequest(url, options, () => fetch(url, {
      ...options,
      headers,
    }));
  }, []);
}

export function useDemoStatus(pollIntervalMs: number = 5000): string | null {
  const [status, setStatus] = useState<string | null>(() => getGravityUserStatus());

  useEffect(() => {
    const runtime = globalThis as {
      setInterval?: typeof setInterval;
      clearInterval?: typeof clearInterval;
    };
    if (typeof runtime.setInterval !== 'function' || typeof runtime.clearInterval !== 'function') {
      return;
    }

    const intervalId = runtime.setInterval(() => {
      setStatus((previous) => {
        const next = getGravityUserStatus();
        return previous === next ? previous : next;
      });
    }, pollIntervalMs);

    return () => runtime.clearInterval?.(intervalId);
  }, [pollIntervalMs]);

  return status;
}

/**
 * Extracts the user id (uid) from the gravity_token JWT if present and valid enough to parse.
 */
export function getGravityUserId(): string | null {
  return getGravityUserIdFromCookie();
}

/**
 * Extracts the user status from the gravity_token JWT if present and valid enough to parse.
 */
export function getGravityUserStatus(): string | null {
  return getGravityUserStatusFromCookie();
}

/**
 * Extracts the user plan from the gravity_token JWT if present and valid enough to parse.
 */
export function getGravityUserPlan(): string | null {
  return getGravityUserPlanFromCookie();
}

/**
 * Extracts the user region from the gravity_token JWT if present and valid enough to parse.
 */
export function getGravityUserRegion(): string | null {
  return getGravityUserRegionFromCookie();
}

export function getGravityUserOrgId(): string | null {
  return getGravityUserOrgIdFromCookie();
}

export function useAuthInitialization() {
  const { initializeAuth } = useAuthStore();
  
  return {
    initializeAuth,
  };
}
